/**
 * @module cercaliagl/view/VGasStationPopup
 */

import $ from 'jquery';
import BaseView from './BaseView';

/**
 * @typedef {Object} GasStationInfo
 * @property {string} [id]
 * @property {import("../servers.js").GeEntity} [ge]
 * @property {string} [info]
 * @property {string} [name]
 * @property {string} [schedule]
 * @property {string} [address]
 * @property {string} [locality]
 * @property {string} [municipality]
 * @property {string} [subregion]
 * @property {string} [gas95]
 * @property {string} [gas98]
 * @property {string} [diesel]
 * @property {string} [biodiesel]
 * @property {string} [gnc]
 * @property {string} [gnl]
 * @property {string} [glp]
 * @property {boolean} [showFont]
 */

/**
 * @enum {string}
 */
export const EventType = {
  DIRECTION_TO: 'direction_to',
};

/**
 * @classdesc
 */
class VGasStationPopup extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $('<div class="v-gasstationpopup mdc-elevation--z4"></div>');

    super(jqElement);

    /**
     * @private
     * @type {import("../LonLat.js").default|undefined}
     */
    this.position_ = undefined;

    /**
     * @private
     * @type {GasStationInfo}
     */
    this.info_ = {};
  }

  /**
   * @private
   */
  render_() {
    const html = `
    <div class="header">
      <span>${this.info_.name}</span>
    </div>
    <div class="gas-content">
      <div class="address">
        <p>
        ${this.info_.address ? `${this.info_.address}<br/>` : ''}
        ${this.info_.locality ? `${this.info_.locality}<br/>` : ''}
        ${this.info_.subregion ? `${this.info_.subregion}` : ''}
        </p>
      </div>
      <div class="prices">
      ${this.info_.schedule ? ` 
        <div>
          <span class="icon-schedule"></span>
          <span class="text">${this.info_.schedule}</span>
        </div>` : ''}
        ${this.info_.gas95 ? `
        <div>
          <span class="icon-gas95"></span>
          <span class="text">${this.info_.gas95}€/l</span>
        </div>` : ''}
        ${this.info_.gas98 ? `
        <div>
          <span class="icon-gas98"></span>
          <span class="text">${this.info_.gas98}€/l</span>
        </div>` : ''}
        ${this.info_.diesel ? `
        <div>
          <span class="icon-diesel"></span>
          <span class="text">${this.info_.diesel}€/l</span>
        </div>` : ''}
        ${this.info_.biodiesel ? `
        <div>
          <span class="icon-biodiesel"></span>
          <span class="text">${this.info_.biodiesel}€/l</span>
        </div>` : ''}
        ${this.info_.gnc ? `
        <div>
          <span class="icon-gnc"></span>
          <span class="text">${this.info_.gnc}€/l</span>
        </div>` : ''}
        ${this.info_.gnl ? `
        <div>
          <span class="icon-gnl"></span>
          <span class="text">${this.info_.gnl}€/l</span>
        </div>` : ''}
        ${this.info_.glp ? `
        <div>
          <span class="icon-glp"></span>
          <span class="text">${this.info_.glp}€/l</span>
        </div>` : ''}
      </div>
      ${this.info_.showFont ? `
      <div class="font">
        <span>Ministerio de Industria, Energía y Turismo</span>
      </div>` : ''}
    </div>
    <div class="actions">
      <button type="button" class="mdc-icon-button route">
        <span class="mdc-icon-button__icon icon-direction"></span>
      </button>
      <button type="button" class="mdc-button ok"><span class="mdc-button__ripple"></span>OK</button>
    </div>`;

    this.jqElement_
      .empty()
      .append(html);

    this.show();
    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    this.jqElement_.find('button.route').click(() => this.emit(EventType.DIRECTION_TO, this.position_));
    this.jqElement_.find('button.ok').click(() => this.hide());
  }

  /**
   * @param {import("../LonLat.js").default} position Positoin
   * @param {string} name GasStation name
   * @param {string} info GasStation Info
   */
  update(position, name, info) {
    const arrInfo = info.split('|');
    this.position_ = position;
    this.info_.name = name;
    /* eslint-disable prefer-destructuring */
    this.info_.address = arrInfo[1];
    this.info_.locality = arrInfo[2];
    this.info_.municipality = arrInfo[3];
    this.info_.subregion = arrInfo[4];
    this.info_.schedule = arrInfo[6];
    this.info_.gas95 = arrInfo[8];
    this.info_.gas98 = arrInfo[13];
    this.info_.diesel = arrInfo[9];
    this.info_.biodiesel = arrInfo[11];
    this.info_.gnc = arrInfo[12];
    this.info_.gnl = arrInfo[15];
    this.info_.glp = arrInfo[17];
    this.info_.showFont = true;
    /* eslint-enable prefer-destructuring */
    this.render_();
  }

  updateWithNaturalGasStation(position, name, info) {
    const arrInfo = info.split('|');
    this.position_ = position;
    this.info_.name = name;
    /* eslint-disable prefer-destructuring */
    this.info_.address = arrInfo[1];
    this.info_.name = name;
    this.info_.locality = arrInfo[2];
    this.info_.municipality = arrInfo[3];
    this.info_.subregion = arrInfo[4];
    this.info_.schedule = arrInfo[6];
    this.info_.gas95 = null;
    this.info_.gas98 = null;
    this.info_.diesel = null;
    this.info_.biodiesel = null;
    this.info_.gnc = arrInfo[8];
    this.info_.gnl = arrInfo[9];
    this.info_.glp = arrInfo[10];
    this.info_.showFont = true;
    /* eslint-enable prefer-destructuring */
    this.render_();
  }

  updateWithElectricCharger(position, name, info) {
    const arrInfo = info.split('|');
    // eslint-disable-next-line prefer-destructuring
    this.info_.address = arrInfo[0];
    this.info_.name = name;
    this.info_.locality = null;
    this.info_.municipality = null;
    this.info_.subregion = null;
    this.info_.schedule = null;
    this.info_.gas95 = null;
    this.info_.gas98 = null;
    this.info_.diesel = null;
    this.info_.biodiesel = null;
    this.info_.gnc = null;
    this.info_.gnl = null;
    this.info_.glp = null;
    this.info_.showFont = false;
    this.render_();
  }
}

export default VGasStationPopup;
